<template>
  <b-card>
    <b-container class="order-created">
      <b-row class="d-flex justify-content-between">
        <BuyOrderText
          :ad-order-entity="adOrderEntity"
          :remaining-time="remainingTime"
        />
      </b-row>

      <b-row>
        <b-col lg="8" sm="12" class="order-created-left pt-2 pr-0 pl-0 pr-lg-2 plg-0">
          <div class="mr-0">
            <AmountSection :amount-details="adOrderEntity" />
          </div>

          <div class="mr-0">
            <PaymentInfo :payment-details="adOrderEntity" />
          </div>

          <div
            v-if="[
              'INITIATED',
              'PROCESSING',
              'CANCELLED',
              'AGREEMENT_REACHED',
              'AGREEMENT_REACHED_REQUEST',
              'NEGOTIATION_FAILED',
              'UNDER_REVIEW',
              'ACCEPTED',
              'REJECTED',
              'RESOLVED',
            ].includes(adOrderEntity.disputeStatus)"
            class="mr-0"
          >
            <DisputeStatusView :dispute-details="adOrderEntity" />
          </div>

          <div
            v-if="adOrderEntity.orderType === 'BUY' && adOrderEntity.orderStatus === 'CREATED'"
            class="mr-0"
          >
            <b-col class="border rounded mt-2 p-1">
              <div class="payment-show">
                <div class="order-created-text mr-2">
                  {{ $t("orderCreated.AfterPaymentTransfer") }}
                </div>
              </div>
            </b-col>
          </div>

          <div
            v-if="adOrderEntity.orderType === 'SELL' && adOrderEntity.orderStatus === 'PAYMENT_SENT'"
            class="mr-0"
          >
            <b-col class="border rounded mt-2 p-1">
              <div class="payment-method d-inline-block">
                {{ $t("orderCreated.ConfirmPaymentIsReceived") }}
              </div>
              <div class="payment-show">
                <div class="order-created-text mr-2">
                  {{ $t("orderCreated.BeforePaymentTransfer") }}
                </div>
              </div>
            </b-col>
          </div>

          <div
            v-if="adOrderEntity.orderType === 'BUY'"
            class="mr-0 mt-2 d-flex"
          >
            <div v-if="adOrderEntity.orderStatus === 'CREATED'" class="text-center">
              <b-button
                variant="primary"
                class="min-120-btn mr-1 mb-1"
                @click="paymentReceivedPop(true, 'BUY')"
              >
                {{ $t("dispute.PaymentSent") }}
              </b-button>
              <b-button
                variant="outline-secondary"
                class="mb-1 min-120-btn"
                @click="cancelOrderPop(true)"
              >
                {{ $t("dispute.CancelOrder") }}
              </b-button>
            </div>

            <div v-if="adOrderEntity.orderStatus === 'PAYMENT_SENT'">
              <b-button
                :disabled="isDisabled"
                variant="secondary"
                @click="disputePop(true)"
              >
                {{ $t("dispute.RaiseDispute") }}
              </b-button>
            </div>

            <RatingButtons
              v-if="['COMPLETED', 'CANCELLED'].includes(adOrderEntity.orderStatus)"
              :user-order-feedback="userOrderFeedback"
              :counter-party-order-feedback="counterPartyOrderFeedback"
            />
          </div>

          <div
            v-if="adOrderEntity.orderType === 'SELL'"
            class="mr-0 mt-2"
          >
            <b-button
              v-if="adOrderEntity.orderStatus === 'CREATED'"
              :disabled="isDisabled"
              variant="secondary"
              @click="disputePop(true)"
            >
              {{ $t("dispute.RaiseDispute") }}
            </b-button>

            <div v-if="['PAYMENT_SENT', 'IN_DISPUTE'].includes(adOrderEntity.orderStatus)">
              <b-button
                variant="primary"
                class="mr-2"
                @click="paymentReceivedPop(true, 'SELL')"
              >
                {{ $t("dispute.PaymentReceived") }}
              </b-button>
              <b-button
                v-if="adOrderEntity.orderStatus === 'PAYMENT_SENT'"
                class="RaiseDispute"
                :disabled="isDisabled"
                variant="secondary"
                @click="disputePop(true)"
              >
                {{ $t("dispute.RaiseDispute") }}
              </b-button>
            </div>

            <RatingButtons
              v-if="['COMPLETED', 'CANCELLED'].includes(adOrderEntity.orderStatus)"
              :user-order-feedback="userOrderFeedback"
              :counter-party-order-feedback="counterPartyOrderFeedback"
            />
          </div>
        </b-col>

        <b-col
          lg="4"
          sm="12"
          class="trusted-rudra mobile-hide mt-2 pr-0 pl-0 plg-0"
        >
          <Chat :counter-user-data="counterUserData" />
        </b-col>
      </b-row>
    </b-container>

    <CancelOrder
      cancel-type=""
      :show-pop="showCancelOrderPop"
      :on-modal-hidden="cancelOrderPop"
    />

    <ConfirmPop
      :show-pop="showPaymentReceivedPop"
      :title="title"
      :on-modal-hidden="paymentReceivedPop"
      :sub-title="subTitle"
      :api-call="apiCall"
      :button-text="buttonText"
      :type="type"
    />

    <DisputeRaised
      v-if="showDisputeOrderPop"
      :show-pop="showDisputeOrderPop"
      :on-modal-hidden="raisedDisputeOrderPop"
      :ad-order-entity="adOrderEntity"
    />
  </b-card>
</template>

<script>
import { BCard, BContainer, BRow, BCol, BButton, BModal } from 'bootstrap-vue';
import { mapState } from 'vuex';
import APIService from '@/libs/api/api';
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue';
import constants from '@/utils/constants';
import EventBus from '@/EventBus';
import BuyOrderText from './BuyOrderText.vue';
import AmountSection from '@core/components/amount-section/Amount.vue';
import PaymentInfo from '@core/components/amount-section/PaymentInfo.vue';
import DisputeStatusView from '@core/components/amount-section/DisputeStatusView.vue';
import RatingButtons from '@core/components/ratting-buttons/RatingButtons.vue';
import Chat from '@core/components/chat/Message.vue';
import CancelOrder from '@/layouts/components/CancelOrder/CancelOrder.vue';
import ConfirmPop from '@/layouts/components/CancelOrder/ConfirmPop.vue';
import DisputeRaised from '@/layouts/components/CancelOrder/DisputeRaised.vue';

export default {
  name: 'OrderCreated',
  components: {
    BCard,
    BContainer,
    BRow,
    BCol,
    BButton,
    BModal,
    BuyOrderText,
    AmountSection,
    PaymentInfo,
    DisputeStatusView,
    RatingButtons,
    Chat,
    CancelOrder,
    ConfirmPop,
    DisputeRaised,
  },
  data() {
    return {
      showCancelOrderPop: false,
      isLoading: true,
      adOrderEntity: {},
      isPaymentSend: false,
      timer: null,
      endTime: 0,
      remainingTime: '',
      counterUserData: {},
      isDisabled: true,
      showDisputeOrderPop: false,
      intervalId: null,
      title: '',
      subTitle: '',
      buttonText: '',
      type: '',
      showPaymentReceivedPop: false,
      lang: this.$route.params.lang,
      userOrderFeedback: {},
      counterPartyOrderFeedback: {},
    };
  },
  computed: {
    ...mapState('user', ['refData', 'user']),
  },
  created() {
    EventBus.$on('getUserOrderFeedback', this.getUserOrderFeedback);
  },
  mounted() {
    if (this.$route.params.id) {
      this.getOrderDetails();
    } else {
      this.$router.go(-1);
    }
  },
  beforeDestroy() {
    this.clearTimeInterval();
    EventBus.$off('getUserOrderFeedback', this.getUserOrderFeedback);
  },
  methods: {
    async getUserOrderFeedback() {
      try {
        this.isLoading = true;
        const res = await new APIService().api(
          {
            method: 'GET',
            url: `p2p/order/feedback/${this.$route.params.id}`,
          },
          {},
          {
            isLoading: true,
          }
        );
        if (res && (res.userOrderFeedback || res.counterPartyOrderFeedback)) {
          if (res.userOrderFeedback) {
            this.userOrderFeedback = {
              ...res.userOrderFeedback,
              updatedTime: constants.dateFormat(res.userOrderFeedback.updatedTime),
            };
          }
          if (res.counterPartyOrderFeedback) {
            this.counterPartyOrderFeedback = {
              ...res.counterPartyOrderFeedback,
              updatedTime: constants.dateFormat(res.counterPartyOrderFeedback.updatedTime),
            };
          }
        } else if (res && res.error && res.error.message) {
          this.$toast({
            component: ToastificationContent,
            props: {
              title: res.error.message,
              icon: 'EditIcon',
              variant: 'danger',
            },
          });
        }
      } catch (error) {
        this.$toast({
          component: ToastificationContent,
          props: {
            title: error?.message || error || 'Error fetching feedback',
            icon: 'EditIcon',
            variant: 'danger',
          },
        });
      }
    },
    raisedDisputeOrderPop(flag) {
      this.showDisputeOrderPop = flag;
    },
    paymentReceivedPop(flag, type) {
      this.showPaymentReceivedPop = flag;
      if (flag && type === 'BUY') {
        this.title = this.$t('orderCreated.BuyPopTitle', {
          amount: `${this.adOrderEntity.totalAmount} ${this.adOrderEntity.fiat}`,
          name: this.counterUserData?.name || '',
        });
        this.subTitle = this.$t('orderCreated.BuyPopSubTitle');
        this.buttonText = this.$t('orderCreated.BuyPopButtonText');
        this.type = type;
      } else if (flag && type === 'SELL') {
        this.title = this.$t('orderCreated.SellPopTitle');
        this.subTitle = this.$t('orderCreated.SellPopSubTitle', {
          amount: `${this.adOrderEntity.totalAmount} ${this.adOrderEntity.fiat}`,
          name: this.counterUserData?.name || '',
        });
        this.buttonText = this.$t('orderCreated.SellPopButtonText');
        this.type = type;
      } else if (flag && type === 'RECEIVED') {
        this.title = this.$t('orderCreated.SuccessfullyPurchased');
        this.subTitle =
          this.user?.id !== this.adOrderEntity.adUserId
            ? `${this.adOrderEntity.finalQuantity} ${this.adOrderEntity.coin}`
            : `${this.adOrderEntity.totalQuantity} ${this.adOrderEntity.coin}`;
        this.buttonText = this.$t('orderCreated.Ok');
        this.type = type;
      } else if (flag && type === 'SOLD') {
        this.title = this.$t('orderCreated.SuccessfullySold');
        this.subTitle =
          this.user?.id !== this.adOrderEntity.adUserId
            ? `${this.adOrderEntity.finalQuantity} ${this.adOrderEntity.coin}`
            : `${this.adOrderEntity.totalQuantity} ${this.adOrderEntity.coin}`;
        this.buttonText = this.$t('orderCreated.Ok');
        this.type = type;
      } else if (!flag) {
        this.title = '';
        this.subTitle = '';
        this.buttonText = '';
        this.type = '';
      }
    },
    apiCall() {
      if (this.type === 'BUY') {
        this.paymentSent();
      } else if (this.type === 'SELL') {
        this.paymentReceived();
      } else if (this.type === 'RECEIVED' || this.type === 'SOLD') {
        this.paymentReceivedPop(false);
      }
    },
    startFetchStatusInterval() {
      this.intervalId = setInterval(this.getOrderStatus, constants.messageInterval * 1000);
    },
    async getOrderStatus() {
      try {
        this.isLoading = true;
        const res = await new APIService().api(
          {
            method: 'GET',
            url: `p2p/order/${this.$route.params.id}/status`,
          },
          {},
          {}
        );
        if (res && res.orderStatus && this.adOrderEntity.orderStatus !== res.orderStatus) {
          this.adOrderEntity.orderStatus = res.orderStatus;
          if (['COMPLETED', 'CANCELLED'].includes(res.orderStatus)) {
            this.getUserOrderFeedback();
            this.clearTimeInterval();
            if (res.orderStatus === 'COMPLETED') {
              this.paymentReceivedPop(true, 'RECEIVED');
            }
          } else if (res.orderStatus === 'PAYMENT_SENT') {
            this.calculateTime();
          }
        } else if (res && res.error && res.error.message) {
          if (res.error.code && String(res.error.code) === '404') {
            if (this.intervalId) {
              clearInterval(this.intervalId);
            }
            return;
          }
          this.$toast({
            component: ToastificationContent,
            props: {
              title: res.error.message,
              icon: 'EditIcon',
              variant: 'danger',
            },
          });
        }
      } catch (error) {
        this.$toast({
          component: ToastificationContent,
          props: {
            title: error?.message || error || 'Error fetching order status',
            icon: 'EditIcon',
            variant: 'danger',
          },
        });
      }
    },
    clearTimeInterval() {
      if (this.timer) {
        clearInterval(this.timer);
      }
      if (this.intervalId) {
        clearInterval(this.intervalId);
      }
    },
    async paymentSent() {
      try {
        this.isLoading = true;
        const res = await new APIService().api(
          {
            method: 'post',
            url: 'p2p/order/markAsPaymentSent',
          },
          {
            orderId: this.$route.params.id,
          },
          {
            isLoading: true,
          }
        );
        if (res && res.id) {
          this.adOrderEntity.orderStatus = res.orderStatus;
          this.paymentReceivedPop(false);
          this.getOrderDetails();
          this.$toast({
            component: ToastificationContent,
            props: {
              title: this.$t('sell.PaymentSuccessfully'),
              icon: 'EditIcon',
              variant: 'success',
            },
          });
        } else if (res && res.error && res.error.message) {
          this.$toast({
            component: ToastificationContent,
            props: {
              title: res.error.message,
              icon: 'EditIcon',
              variant: 'danger',
            },
          });
        }
      } catch (error) {
        this.$toast({
          component: ToastificationContent,
          props: {
            title: error?.message || error || 'Error marking payment sent',
            icon: 'EditIcon',
            variant: 'danger',
          },
        });
      }
      this.isLoading = false;
    },
    async paymentReceived() {
      try {
        this.isLoading = true;
        const res = await new APIService().api(
          {
            method: 'post',
            url: 'p2p/order/markAsPaymentReceived',
          },
          {
            orderId: this.$route.params.id,
          },
          {
            isLoading: true,
          }
        );
        if (res && res.id) {
          this.getOrderDetails();
          await this.paymentReceivedPop(false);
          this.paymentReceivedPop(true, 'SOLD');
          this.$toast({
            component: ToastificationContent,
            props: {
              title: this.$t('sell.PaymentSuccessfullyReceived'),
              icon: 'EditIcon',
              variant: 'success',
            },
          });
        } else if (res && res.error && res.error.message) {
          this.$toast({
            component: ToastificationContent,
            props: {
              title: res.error.message,
              icon: 'EditIcon',
              variant: 'danger',
            },
          });
        }
      } catch (error) {
        this.$toast({
          component: ToastificationContent,
          props: {
            title: error?.message || error || 'Error confirming payment received',
            icon: 'EditIcon',
            variant: 'danger',
          },
        });
      }
      this.isLoading = false;
    },
    calculateTime() {
      if (this.timer) {
        clearInterval(this.timer);
        this.timer = null;
      }
      if (this.adOrderEntity && this.adOrderEntity.orderStatus === 'CREATED') {
        this.isDisabled = true;
        const paymentTimeLimit = Number(this.adOrderEntity.paymentTimeLimit) || 15;
        const rawDate = this.adOrderEntity.createdTime;
        const createdTime = rawDate
          ? new Date(rawDate.endsWith('Z') || rawDate.includes('+') ? rawDate : `${rawDate}Z`).getTime()
          : Date.now();
        const time = paymentTimeLimit * 60 * 1000;
        this.endTime = createdTime + time;
        this.updateRemainingTime(this.endTime);
        this.timer = setInterval(() => {
          this.updateRemainingTime(this.endTime);
        }, 1000);
      } else if (this.adOrderEntity && this.adOrderEntity.orderStatus === 'PAYMENT_SENT') {
        this.isDisabled = true;
        const rawConfirmationTime = this.refData?.paymentConfirmationTime;
        let paymentTimeLimit = 15;
        if (typeof rawConfirmationTime === 'number') {
          paymentTimeLimit = rawConfirmationTime;
        } else if (rawConfirmationTime && typeof rawConfirmationTime === 'object' && rawConfirmationTime.value !== undefined) {
          paymentTimeLimit = Number(rawConfirmationTime.value) || 15;
        } else if (rawConfirmationTime && !isNaN(Number(rawConfirmationTime))) {
          paymentTimeLimit = Number(rawConfirmationTime);
        } else if (this.adOrderEntity.paymentTimeLimit) {
          paymentTimeLimit = Number(this.adOrderEntity.paymentTimeLimit);
        }
        const rawDate = this.adOrderEntity.paymentSentTime || this.adOrderEntity.updatedTime || this.adOrderEntity.createdTime;
        const createdTime = rawDate
          ? new Date(rawDate.endsWith('Z') || rawDate.includes('+') ? rawDate : `${rawDate}Z`).getTime()
          : Date.now();
        this.endTime = createdTime + paymentTimeLimit * 60 * 1000;
        this.updateRemainingTime(this.endTime);
        this.timer = setInterval(() => {
          this.updateRemainingTime(this.endTime);
        }, 1000);
      }
    },
    updateRemainingTime(endTime) {
      const currentTime = Date.now();
      const remainingTime = Math.max(0, this.endTime - currentTime);
      const hours = Math.floor(remainingTime / (1000 * 60 * 60));
      const minutes = Math.floor((remainingTime % (1000 * 60 * 60)) / (1000 * 60));
      const seconds = Math.floor((remainingTime % (1000 * 60)) / 1000);
      this.remainingTime = `${this.padZero(hours)}:${this.padZero(minutes)}:${this.padZero(seconds)}`;
      if (!hours && !minutes && !seconds) {
        if (this.timer) {
          clearInterval(this.timer);
          this.timer = null;
          this.isDisabled = false;
        }
      }
    },
    padZero(num) {
      return num < 10 ? `0${num}` : num;
    },
    cancelOrderPop(flag, isGetOrderCall) {
      this.showCancelOrderPop = flag;
      if (isGetOrderCall) {
        this.getOrderDetails();
      }
    },
    disputePop(flag) {
      this.showDisputeOrderPop = flag;
    },
    async getOrderDetails() {
      try {
        this.isLoading = true;
        const res = await new APIService().api(
          {
            method: 'GET',
            url: `p2p/order/adOrder/${this.$route.params.id}`,
          },
          {},
          {
            isLoading: true,
          }
        );
        if (res && res.adOrderEntity) {
          this.adOrderEntity = res.adOrderEntity;
          if (res.counterUserData) {
            this.adOrderEntity.username = res.counterUserData.name || res.counterUserData.email;
            this.counterUserData = res.counterUserData;
          }
          this.calculateTime();
          if (res.adOrderEntity.orderType === 'BUY' && res.adOrderEntity.orderStatus === 'PAYMENT_SENT') {
            this.startFetchStatusInterval();
          }
          if (
            res.adOrderEntity.orderType === 'SELL' &&
            ['PAYMENT_SENT', 'CREATED'].includes(res.adOrderEntity.orderStatus)
          ) {
            this.startFetchStatusInterval();
          } else if (['COMPLETED', 'CANCELLED'].includes(res.adOrderEntity.orderStatus)) {
            this.getUserOrderFeedback();
          }
        } else if (res && res.error && res.error.message) {
          this.$toast({
            component: ToastificationContent,
            props: {
              title: res.error.message,
              icon: 'EditIcon',
              variant: 'danger',
            },
          });
        }
      } catch (error) {
        this.$toast({
          component: ToastificationContent,
          props: {
            title: error?.message || String(error),
            icon: 'EditIcon',
            variant: 'danger',
          },
        });
      }
    },
  },
};
</script>

<style scoped>
.payment-show {
  display: flex;
  align-items: center;
}
.timer-color {
  color: var(--primary);
}
.order-created .order-created-text {
  color: #707a8a;
  font-size: 14px;
  font-weight: 500;
  line-height: 20px;
}
.order-created .payment-method {
  color: var(--text-black-color);
  font-size: 19px;
  font-weight: 600;
  line-height: 24px;
}
.order-created .vertical-red-line {
  display: inline-block;
  width: 2px;
  height: 12px;
}
[dir] .order-created .vertical-red-line {
  border: 2px solid #f7984a;
  margin: 0px 5px -2px 5px;
  border-radius: 4px;
  background: #f7984a;
}
.order-created .trusted-rudra {
  height: fit-content;
}
[dir] .order-created .trusted-rudra {
  background-color: transparent;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
  border-radius: 5px;
  padding: 0;
}
.dispute {
  color: var(--text-black-color);
  font-size: 19px;
  font-weight: 600;
  line-height: 24px;
}
.dispute-text {
  color: var(--text-black-color);
  font-size: 14px;
  font-weight: 500;
  line-height: 22px;
}
.ol-text {
  color: var(--text-black-color);
  font-size: 13px;
  font-weight: 400;
  line-height: 24px;
}
@media (min-width: 430px) and (max-width: 991px) {
  [dir] .order-num {
    margin-top: 16px;
  }
}
@media (min-width: 991px) {
  .chat {
    display: none;
  }
}
@media (max-width: 991px) {
  .chat {
    width: 10vw;
    height: 5vh;
  }
}
</style>
